import React, { useMemo, useRef } from "react";
import {
  DEFAULT_IMAGE_FRAME,
} from "../../../../../components/shared/imageToolbar/imageCropModel";
import {
  CIRCLE_CROP_FIXED_FRAME,
  CIRCLE_CROP_BG_TYPES,
  CIRCLE_CROP_MULTICOLOR_GRADIENT,
  clampCircleCropBorderWidth,
  normalizeCircleCropBackgroundType,
  clampCircleCropRotation,
  moveCircleCropImageFrame,
  resolveCircleCropBorderColor,
  resolveCircleCropPrepFrames,
} from "../../../lib/circleCropPrepModel";
import "./CircleCropOverlay.css";

const pctBox = (frame) => ({
  left: `${frame.x * 100}%`,
  top: `${frame.y * 100}%`,
  width: `${frame.w * 100}%`,
  height: `${frame.h * 100}%`,
});

/**
 * Fixed square viewport — pan/zoom image inside a circular mask with bg + border.
 */
const CircleCropOverlay = ({
  imageUrl,
  settings = null,
  width,
  height,
  onFramesChange = () => {},
  disabled = false,
}) => {
  const hostRef = useRef(null);
  const dragRef = useRef(null);

  const cropFrame = CIRCLE_CROP_FIXED_FRAME;
  const imageFrame = settings?.imageFrame || DEFAULT_IMAGE_FRAME;
  const backgroundType = normalizeCircleCropBackgroundType(
    settings?.backgroundType
  );
  const borderWidth = clampCircleCropBorderWidth(settings?.borderWidth);
  const borderColor = resolveCircleCropBorderColor(settings);
  const customBgColor = settings?.backgroundColor || "#ffffff";
  const borderPx = Math.max(0, Math.round((Math.max(width, 1) * borderWidth) / 100));
  const innerRadiusPct = Math.max(0, 50 - borderWidth);
  const rotation = clampCircleCropRotation(settings?.rotation);
  const flipH = Boolean(settings?.flipH);
  const flipV = Boolean(settings?.flipV);

  const { imageFrame: image } = resolveCircleCropPrepFrames(
    cropFrame,
    imageFrame,
    width,
    height
  );

  const imageRotateStyle = useMemo(() => {
    const originX = image.w > 0 ? ((0.5 - image.x) / image.w) * 100 : 50;
    const originY = image.h > 0 ? ((0.5 - image.y) / image.h) * 100 : 50;
    const transforms = [];
    if (rotation) transforms.push(`rotate(${rotation}deg)`);
    if (flipH) transforms.push("scaleX(-1)");
    if (flipV) transforms.push("scaleY(-1)");
    return {
      transform: transforms.length ? transforms.join(" ") : undefined,
      transformOrigin: `${originX}% ${originY}%`,
    };
  }, [flipH, flipV, image.h, image.w, image.x, image.y, rotation]);

  const overlayStyle = useMemo(
    () => ({
      width,
      height,
      "--ccrp-border-px": `${borderPx}px`,
      "--ccrp-border-color": borderColor,
      "--ccrp-inner-radius": `${innerRadiusPct}%`,
    }),
    [borderColor, borderPx, height, innerRadiusPct, width]
  );

  const bgLayerClass = useMemo(() => {
    const base = "circle-crop-overlay__bg";
    if (backgroundType === CIRCLE_CROP_BG_TYPES.TRANSPARENT) {
      return `${base} circle-crop-overlay__bg--checker`;
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.BLUR) {
      return `${base} circle-crop-overlay__bg--blur`;
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.MULTICOLOR) {
      return `${base} circle-crop-overlay__bg--multicolor`;
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.WHITE) {
      return `${base} circle-crop-overlay__bg--solid`;
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.BLACK) {
      return `${base} circle-crop-overlay__bg--solid`;
    }
    return `${base} circle-crop-overlay__bg--solid`;
  }, [backgroundType]);

  const bgLayerStyle = useMemo(() => {
    if (backgroundType === CIRCLE_CROP_BG_TYPES.MULTICOLOR) {
      return { background: CIRCLE_CROP_MULTICOLOR_GRADIENT };
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.WHITE) {
      return { backgroundColor: "#ffffff" };
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.BLACK) {
      return { backgroundColor: "#000000" };
    }
    if (backgroundType === CIRCLE_CROP_BG_TYPES.CUSTOM) {
      return { backgroundColor: customBgColor };
    }
    return undefined;
  }, [backgroundType, customBgColor]);

  const emitImageFrame = (nextImage) => {
    const resolved = resolveCircleCropPrepFrames(
      cropFrame,
      nextImage,
      width,
      height
    );
    onFramesChange(resolved.cropFrame, resolved.imageFrame);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || disabled) return;
    const host = hostRef.current;
    if (!host) return;
    const bounds = host.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const dx = (e.clientX - drag.startClientX) / bounds.width;
    const dy = (e.clientY - drag.startClientY) / bounds.height;
    emitImageFrame(
      moveCircleCropImageFrame(drag.startImage, dx, dy, cropFrame)
    );
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    dragRef.current = null;
  };

  const startPan = (e) => {
    if (disabled) return;
    e.preventDefault();
    dragRef.current = {
      startImage: { ...image },
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  };

  return (
    <div
      className="circle-crop-overlay-wrap"
      style={{ width, height }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        ref={hostRef}
        className="circle-crop-overlay"
        style={overlayStyle}
        onPointerDown={startPan}
      >
        <div className="circle-crop-overlay__clip">
          <div className={bgLayerClass} style={bgLayerStyle}>
            {backgroundType === CIRCLE_CROP_BG_TYPES.BLUR && imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="circle-crop-overlay__blur-img"
                draggable={false}
              />
            ) : null}
          </div>
          <div
            className="circle-crop-overlay__image-layer"
            style={pctBox(image)}
          >
            <img
              src={imageUrl}
              alt=""
              className="circle-crop-overlay__img"
              style={imageRotateStyle}
              draggable={false}
            />
          </div>
        </div>
        <div
          className="circle-crop-overlay__border"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default CircleCropOverlay;
